import { useSyncExternalStore } from 'react';
import { storage } from './api.js';

export type Locale = 'en' | 'ja';
const requested = new URLSearchParams(location.search).get('lang');
const saved = storage.get('ld.locale');
let locale: Locale =
  requested === 'ja' || requested === 'en'
    ? requested
    : saved === 'ja' || saved === 'en'
      ? saved
      : navigator.language.startsWith('ja')
        ? 'ja'
        : 'en';
const listeners = new Set<() => void>();
document.documentElement.lang = locale;

export const t = (en: string, ja: string): string => (locale === 'ja' ? ja : en);
export const currentLocale = () => locale;
export function setLocale(next: Locale) {
  locale = next;
  storage.set('ld.locale', next);
  document.documentElement.lang = next;
  // Explicit language links apply once; the switch remains effective afterward.
  const url = new URL(location.href);
  url.searchParams.delete('lang');
  history.replaceState(history.state, '', url);
  listeners.forEach((fn) => fn());
}
export function useLocale(): Locale {
  return useSyncExternalStore((fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, currentLocale);
}

const errors: Record<string, [string, string]> = {
  audio_failed: [
    'Audio stopped. Tap Retry interrupted audio to continue.',
    '音声が停止しました。「中断した音声を再試行」で再生できます。',
  ],
  room_not_found: [
    'This room has closed or the code is incorrect. Try a solo round or join another room.',
    '部屋が終了したか、コードが間違っています。一人で遊ぶか、別の部屋に参加してください。',
  ],
  room_full: [
    'This room is full. Try a solo round while you wait, or ask your host for another room.',
    'この部屋は満員です。一人で遊ぶか、ホストに別の部屋を確認してください。',
  ],
  unauthorized: [
    'Open the room in the browser that created it, or create a new room.',
    '部屋を作ったブラウザで開くか、新しい部屋を作ってください。',
  ],
  kicked: ['The host removed you from this room.', 'ホストによって退室しました。'],
  name_taken: [
    'That name is already in use. Choose another name.',
    'その名前は使われています。別の名前を選んでください。',
  ],
  rate_limited: [
    'Too many requests. Please wait a minute and try again.',
    'リクエストが多すぎます。1 分待ってからお試しください。',
  ],
  wrong_phase: [
    'The round has moved on. Please wait for the next step.',
    'ラウンドが進みました。次のステップまでお待ちください。',
  ],
  not_allowed: [
    'That action is not available now. Your first answer is final.',
    '今はその操作ができません。最初の回答で確定します。',
  ],
};
export function errorMessage(code: string): string {
  const pair = errors[code];
  return pair
    ? t(...pair)
    : t(
        'Something went wrong. Check your connection and try again.',
        '接続を確認して、もう一度お試しください。',
      );
}
